import React, { useEffect, useState, useCallback } from 'react';
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Upload as UploadIcon,
  X,
  Eye,
  EyeOff,
  ImageOff,
  Star,
  StarOff,
  Music as MusicIcon,
} from 'lucide-react';
import api, { resolveImageUrl } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';

const emptySocials = {
  spotify: '',
  youtube: '',
  instagram: '',
  tiktok: '',
  twitter: '',
  website: '',
};

const emptyForm = {
  id: null,
  name: '',
  photo: '',
  bio: '',
  genre: 'Pop',
  hometown: '',
  socials: { ...emptySocials },
  discography: [],
  featured: false,
  published: true,
};

const GENRES = ['Pop', 'Dangdut', 'Daerah', 'Jawa', 'Bali', 'Hip Hop', 'Religi', 'Rock', 'EDM', 'Jazz', 'Lainnya'];

const emptyDiscItem = { title: '', release_date: '', cover: '', url: '' };

const ArtistsManager = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [discUploadIdx, setDiscUploadIdx] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/artists');
      setItems(data);
    } catch {
      toast({ title: 'Gagal memuat data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setModal(true);
  };

  const openEdit = (a) => {
    setForm({
      ...emptyForm,
      ...a,
      socials: { ...emptySocials, ...(a.socials || {}) },
      discography: (a.discography || []).map((d) => ({
        title: d.title || '',
        release_date: (d.release_date || '').slice(0, 10),
        cover: d.cover || '',
        url: d.url || '',
      })),
    });
    setModal(true);
  };

  const uploadFile = useCallback(async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post('/admin/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.url;
  }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, photo: url }));
      toast({ title: 'Foto berhasil di-upload' });
    } catch (err) {
      toast({
        title: 'Upload gagal',
        description: err?.response?.data?.detail || 'Terjadi kesalahan',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDiscCoverUpload = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDiscUploadIdx(idx);
    try {
      const url = await uploadFile(file);
      setForm((f) => {
        const disc = [...f.discography];
        disc[idx] = { ...disc[idx], cover: url };
        return { ...f, discography: disc };
      });
    } catch (err) {
      toast({
        title: 'Upload gagal',
        description: err?.response?.data?.detail || 'Terjadi kesalahan',
        variant: 'destructive',
      });
    } finally {
      setDiscUploadIdx(null);
    }
  };

  const addDiscItem = () =>
    setForm((f) => ({ ...f, discography: [...f.discography, { ...emptyDiscItem }] }));

  const removeDiscItem = (idx) =>
    setForm((f) => ({ ...f, discography: f.discography.filter((_, i) => i !== idx) }));

  const updateDiscItem = (idx, key, value) =>
    setForm((f) => {
      const disc = [...f.discography];
      disc[idx] = { ...disc[idx], [key]: value };
      return { ...f, discography: disc };
    });

  const updateSocial = (key, value) =>
    setForm((f) => ({ ...f, socials: { ...f.socials, [key]: value } }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Nama artis wajib diisi', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.slug;
      delete payload.created_at;
      delete payload.updated_at;

      if (form.id) {
        await api.put(`/admin/artists/${form.id}`, payload);
        toast({ title: 'Artis diperbarui' });
      } else {
        await api.post('/admin/artists', payload);
        toast({ title: 'Artis ditambahkan' });
      }
      setModal(false);
      await load();
    } catch (err) {
      toast({
        title: 'Gagal menyimpan',
        description: err?.response?.data?.detail || 'Terjadi kesalahan',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus artis ini?')) return;
    try {
      await api.delete(`/admin/artists/${id}`);
      toast({ title: 'Artis dihapus' });
      await load();
    } catch {
      toast({ title: 'Gagal menghapus', variant: 'destructive' });
    }
  };

  const togglePublished = async (a) => {
    try {
      await api.put(`/admin/artists/${a.id}`, { published: !a.published });
      await load();
    } catch {
      toast({ title: 'Gagal mengubah status', variant: 'destructive' });
    }
  };

  const toggleFeatured = async (a) => {
    try {
      await api.put(`/admin/artists/${a.id}`, { featured: !a.featured });
      await load();
    } catch {
      toast({ title: 'Gagal mengubah featured', variant: 'destructive' });
    }
  };

  const SOCIAL_KEYS = [
    ['spotify', 'Spotify'],
    ['youtube', 'YouTube'],
    ['instagram', 'Instagram'],
    ['tiktok', 'TikTok'],
    ['twitter', 'Twitter/X'],
    ['website', 'Website'],
  ];

  return (
    <div data-testid="artists-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-white font-bold text-2xl tracking-tight">Artis Roster</h2>
          <p className="text-neutral-500 text-sm mt-1">
            Kelola profil artis binaan (foto, bio, discography, socials)
          </p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="artists-add-btn"
          className="rounded-full bg-gradient-to-r from-[#E9C46A] to-[#D4A94F] text-black font-semibold btn-transition gold-shimmer"
        >
          <Plus className="w-4 h-4 mr-1" /> Tambah Artis
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-[#E9C46A] animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.length === 0 && (
            <div className="col-span-full text-center py-16 text-neutral-500 border border-dashed border-white/10 rounded-2xl">
              Belum ada artis. Klik &quot;Tambah Artis&quot; untuk memulai.
            </div>
          )}
          {items.map((a) => (
            <div
              key={a.id}
              className="bg-[#0f0f0f] border border-white/[0.06] rounded-2xl p-4 flex gap-4"
              data-testid={`artist-row-${a.slug}`}
            >
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-neutral-900 flex-shrink-0 flex items-center justify-center">
                {a.photo ? (
                  <img
                    src={resolveImageUrl(a.photo)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <MusicIcon className="w-6 h-6 text-neutral-700" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold truncate">{a.name}</h3>
                    <p className="text-neutral-500 text-xs truncate mt-0.5">
                      {a.hometown} · {(a.discography || []).length} rilisan
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs">
                      <span className="px-2 py-0.5 rounded-full bg-[#D4A94F]/15 text-[#E9C46A] font-semibold">
                        {a.genre}
                      </span>
                      {a.featured && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 font-semibold flex items-center gap-1">
                          <Star className="w-3 h-3 fill-amber-300" /> Featured
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      a.published
                        ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25'
                        : 'bg-neutral-500/15 text-neutral-400 border border-neutral-500/25'
                    }`}
                  >
                    {a.published ? 'Live' : 'Draft'}
                  </span>
                </div>
                <div className="flex items-center gap-1 mt-3">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 px-2 text-neutral-400 hover:text-[#E9C46A]"
                    onClick={() => togglePublished(a)}
                  >
                    {a.published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 px-2 text-neutral-400 hover:text-amber-300"
                    onClick={() => toggleFeatured(a)}
                  >
                    {a.featured ? <StarOff className="w-3.5 h-3.5" /> : <Star className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 px-2 text-neutral-400 hover:text-[#E9C46A]"
                    onClick={() => openEdit(a)}
                    data-testid={`artist-edit-${a.slug}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 px-2 text-neutral-400 hover:text-red-400"
                    onClick={() => handleDelete(a.id)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div
          className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0d0d0d] border border-[#D4A94F]/30 rounded-3xl max-w-4xl w-full max-h-[92vh] overflow-y-auto"
            data-testid="artists-modal"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/[0.06] sticky top-0 bg-[#0d0d0d] z-10">
              <h3 className="text-white font-bold text-xl">
                {form.id ? 'Edit Artis' : 'Artis Baru'}
              </h3>
              <button onClick={() => setModal(false)} className="text-neutral-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Basic info */}
              <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-5">
                {/* Photo */}
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                    Foto Artis
                  </label>
                  <div className="mt-1.5 aspect-square rounded-2xl overflow-hidden bg-neutral-900 border border-white/10 flex items-center justify-center relative">
                    {form.photo ? (
                      <img
                        src={resolveImageUrl(form.photo)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImageOff className="w-10 h-10 text-neutral-700" />
                    )}
                  </div>
                  <div className="mt-2 space-y-2">
                    <Input
                      value={form.photo}
                      onChange={(e) => setForm({ ...form, photo: e.target.value })}
                      placeholder="URL foto atau upload"
                      className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white text-xs"
                    />
                    <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#D4A94F]/15 border border-[#D4A94F]/30 text-[#E9C46A] text-xs font-semibold cursor-pointer hover:bg-[#D4A94F]/25 btn-transition w-full justify-center">
                      {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <UploadIcon className="w-3 h-3" />}
                      Upload Foto
                      <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                    </label>
                  </div>
                </div>

                {/* Info fields */}
                <div className="space-y-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                      Nama Artis *
                    </label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1.5"
                      data-testid="artist-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                        Genre
                      </label>
                      <select
                        value={form.genre}
                        onChange={(e) => setForm({ ...form, genre: e.target.value })}
                        className="w-full mt-1.5 h-10 rounded-md bg-black/50 border border-white/10 text-white px-3 focus:outline-none focus:ring-2 focus:ring-[#D4A94F]"
                      >
                        {GENRES.map((g) => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                        Asal / Hometown
                      </label>
                      <Input
                        value={form.hometown}
                        onChange={(e) => setForm({ ...form, hometown: e.target.value })}
                        placeholder="Jakarta, Indonesia"
                        className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1.5"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                      Bio
                    </label>
                    <Textarea
                      value={form.bio}
                      onChange={(e) => setForm({ ...form, bio: e.target.value })}
                      rows={5}
                      placeholder="Ceritakan tentang artis ini..."
                      className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1.5 resize-none"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-5">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.published}
                        onChange={(e) => setForm({ ...form, published: e.target.checked })}
                        className="w-4 h-4 accent-[#D4A94F]"
                      />
                      <span className="text-sm text-neutral-300">Publish</span>
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.featured}
                        onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                        className="w-4 h-4 accent-[#D4A94F]"
                      />
                      <span className="text-sm text-neutral-300 flex items-center gap-1.5">
                        <Star className="w-3.5 h-3.5 text-amber-400" /> Featured di halaman /artis
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Socials */}
              <div>
                <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-2 block">
                  Sosial Media & Link
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {SOCIAL_KEYS.map(([key, label]) => (
                    <div key={key}>
                      <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                        {label}
                      </label>
                      <Input
                        value={form.socials?.[key] || ''}
                        onChange={(e) => updateSocial(key, e.target.value)}
                        placeholder={`URL ${label}`}
                        className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Discography */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                    Discography ({form.discography.length})
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={addDiscItem}
                    className="text-[#E9C46A] hover:text-[#E9C46A] hover:bg-[#D4A94F]/10 text-xs"
                    data-testid="artist-add-disc"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Tambah Rilis
                  </Button>
                </div>
                <div className="space-y-3">
                  {form.discography.length === 0 && (
                    <p className="text-neutral-500 text-xs italic">
                      Belum ada rilis. Tambahkan lagu/album beserta cover dan link Spotify/YouTube.
                    </p>
                  )}
                  {form.discography.map((d, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-1 md:grid-cols-[80px_1fr_auto] gap-3 items-start p-3 rounded-xl bg-black/30 border border-white/[0.06]"
                    >
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-neutral-900 flex-shrink-0 flex items-center justify-center border border-white/10 relative">
                        {d.cover ? (
                          <img src={resolveImageUrl(d.cover)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ImageOff className="w-5 h-5 text-neutral-700" />
                        )}
                        <label className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 hover:opacity-100 btn-transition cursor-pointer">
                          {discUploadIdx === idx ? (
                            <Loader2 className="w-4 h-4 animate-spin text-[#E9C46A]" />
                          ) : (
                            <UploadIcon className="w-4 h-4 text-[#E9C46A]" />
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleDiscCoverUpload(idx, e)}
                          />
                        </label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 min-w-0">
                        <Input
                          value={d.title}
                          onChange={(e) => updateDiscItem(idx, 'title', e.target.value)}
                          placeholder="Judul lagu / album"
                          className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white text-sm"
                        />
                        <Input
                          type="date"
                          value={d.release_date}
                          onChange={(e) => updateDiscItem(idx, 'release_date', e.target.value)}
                          className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white text-sm"
                        />
                        <Input
                          value={d.url}
                          onChange={(e) => updateDiscItem(idx, 'url', e.target.value)}
                          placeholder="Link Spotify / YouTube"
                          className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white text-sm md:col-span-2"
                        />
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeDiscItem(idx)}
                        className="text-neutral-500 hover:text-red-400 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 p-6 border-t border-white/[0.06] sticky bottom-0 bg-[#0d0d0d]">
              <Button variant="ghost" onClick={() => setModal(false)} className="text-neutral-400 hover:text-white">
                Batal
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="rounded-full bg-gradient-to-r from-[#E9C46A] to-[#D4A94F] text-black font-semibold btn-transition"
                data-testid="artist-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Simpan'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistsManager;
